import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CLIENT_DATA } from '../data/clients';
import { getProviderFields } from '../data/providers';
import type { Provider } from '../types';

interface ConnectionFormProps {
	provider: Provider;
	onBack: () => void;
	onNext: () => void;
}

export function ConnectionForm({
	provider,
	onBack,
	onNext,
}: ConnectionFormProps) {
	const fields = getProviderFields(provider.key);

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-3 rounded-lg border bg-muted/20 p-3">
				<span className="flex h-8 w-8 items-center justify-center rounded bg-muted text-xs font-bold">
					{provider.logo}
				</span>
				<div>
					<p className="font-medium">{provider.name}</p>
					<p className="text-xs text-muted-foreground">{provider.desc}</p>
				</div>
			</div>

			<div>
				<Label>
					Client ID <span className="text-red-500">*</span>
				</Label>
				<Select>
					<SelectTrigger className="mt-1">
						<SelectValue placeholder="Select Client" />
					</SelectTrigger>
					<SelectContent>
						{CLIENT_DATA.map((c) => (
							<SelectItem key={c.id} value={c.clientId}>
								{c.clientId} — {c.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<p className="mt-1 text-xs text-muted-foreground">
					Map this connection to a Connectra client
				</p>
			</div>

			{fields.map((f, i) => (
				<div key={i}>
					<Label>
						{f.label} {f.required && <span className="text-red-500">*</span>}
					</Label>
					{f.selectOptions ? (
						<Select>
							<SelectTrigger className="mt-1">
								<SelectValue placeholder={`Select ${f.label}`} />
							</SelectTrigger>
							<SelectContent>
								{f.selectOptions.map((opt) => (
									<SelectItem key={opt} value={opt}>
										{opt}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : f.textarea ? (
						<Textarea placeholder={f.placeholder} className="mt-1" rows={4} />
					) : (
						<Input
							type={f.password ? 'password' : 'text'}
							placeholder={f.placeholder}
							className="mt-1"
						/>
					)}
					{f.hint && (
						<p className="mt-1 text-xs text-muted-foreground">{f.hint}</p>
					)}
				</div>
			))}

			<div className="flex justify-between">
				<Button variant="ghost" onClick={onBack}>
					← Back
				</Button>
				<div className="flex gap-2">
					<Button variant="outline" onClick={onBack}>
						Cancel
					</Button>
					<Button onClick={onNext}>Next →</Button>
				</div>
			</div>
		</div>
	);
}
